import { FC, useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import AutomasPeritajeEntity from "../../../../domain/entities/AutomasPeritajeEntity";
import GetCarAutomasByPlateApiImpl from "../../../../data/repositories/automas/GetCarAutomasByPlateApiImpl";
import CertificadoPeritajeAutomas from "./CertificadoPeritajeAutomas";
import { maskPlate } from "./utils";
import "./ModalCertificadoPeritajeAutomasStyles.scss";

interface ModalCertificadoPeritajeAutomasProps {
    /** Placa a consultar en `get-car-automas/<placa>/`. Se ignora si llega `data`. */
    plate?: string;
    /** Peritaje ya resuelto (por ejemplo el mock de la pantalla de prueba). */
    data?: AutomasPeritajeEntity | null;
    close: () => void;
    /**
     * Se dispara cuando Automás no tiene peritaje para esta placa, para que quien lo abre pueda
     * caer a otra fuente. Sin este callback se muestra el mensaje de "no disponible".
     */
    onNoData?: () => void;
}

const ModalCertificadoPeritajeAutomas: FC<ModalCertificadoPeritajeAutomasProps> = ({
    plate,
    data,
    close,
    onNoData,
}) => {
    const [peritaje, setPeritaje] = useState<AutomasPeritajeEntity | null>(data ?? null);
    const [loading, setLoading] = useState(!data && !!plate);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (data) {
            setPeritaje(data);
            setLoading(false);
            setError(false);
            return;
        }
        if (!plate) return;

        let active = true;
        setLoading(true);
        setError(false);
        GetCarAutomasByPlateApiImpl(plate).then((response) => {
            if (!active) return;
            if (response) setPeritaje(response);
            else if (onNoData) onNoData();
            else setError(true);
            setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [plate, data, onNoData]);

    // Cerrar con ESC, bloquear el scroll del fondo y habilitar las reglas de impresión
    // (clase en body + `@page` inyectada) solo mientras el modal está abierto.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
        };
        document.addEventListener("keydown", onKeyDown);

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        document.body.classList.add("pa-modal-open");

        const pageRule = document.createElement("style");
        pageRule.setAttribute("data-pa-page-rule", "");
        pageRule.textContent = "@page { size: A4 portrait; margin: 0; }";
        document.head.appendChild(pageRule);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = previousOverflow;
            document.body.classList.remove("pa-modal-open");
            pageRule.remove();
        };
    }, [close]);

    return (
        <div className="pa-overlay" onClick={close}>
            <div className="pa-overlay__inner" onClick={(event) => event.stopPropagation()}>
                <div className="pa-toolbar">
                    <div className="pa-toolbar__title">
                        Certificado de peritaje
                        {peritaje?.plate ? (
                            <span className="pa-toolbar__sub">Placa {maskPlate(peritaje.plate)}</span>
                        ) : null}
                    </div>
                    <div className="pa-toolbar__actions">
                        <button type="button" onClick={close} title="Cerrar">
                            <IoMdClose size={20} />
                        </button>
                    </div>
                </div>

                <div className="pa-overlay__body">
                    {loading && <div className="pa-state">Consultando el peritaje…</div>}
                    {!loading && (error || !peritaje) && (
                        <div className="pa-state">No fue posible cargar el peritaje de este vehículo.</div>
                    )}
                    {!loading && peritaje && <CertificadoPeritajeAutomas data={peritaje} />}
                </div>
            </div>
        </div>
    );
};

export default ModalCertificadoPeritajeAutomas;
